import { useCallback, useEffect, useMemo, useState } from "react";
import { App, Card, Checkbox, Col, DatePicker, Flex, Form, Input, Row, Spin, Typography } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton/Button";
import CustomInput from "../../../components/CustomInput/Input";
import CustomTable from "../../../components/CustomTable/CustomTable";
import {
  clearBankOpsMessages,
  confirmManualWithdrawalsBulk,
  fetchPendingWithdrawals,
} from "../../../redux/slices/bankOperationsSlice";
import {
  formatCurrency,
  getRemarksRules,
  REMARKS_MAX_LENGTH,
  toTransactionDateIso,
  UTR_ALLOWED_PATTERN,
  UTR_MAX_LENGTH,
  UTR_MIN_LENGTH,
} from "../../../helpers/helpers";
import { SHOW_MARK_AS_DUPLICATE } from "../../../constants/manualDuplicate";

const { Text } = Typography;

const validateUtrValue = (utr) => {
  const trimmed = (utr ?? "").trim();
  if (!trimmed) return "UTR is required";
  if (/\s/.test(trimmed)) return "UTR cannot contain spaces";
  if (!UTR_ALLOWED_PATTERN.test(trimmed)) return "UTR can contain only letters and numbers";
  if (/^[A-Za-z]+$/.test(trimmed)) return "UTR cannot contain only alphabets";
  if (trimmed.length < UTR_MIN_LENGTH) return `UTR must be at least ${UTR_MIN_LENGTH} characters`;
  if (trimmed.length > UTR_MAX_LENGTH) return `UTR cannot exceed ${UTR_MAX_LENGTH} characters`;
  return null;
};

const WithdrawalPayoutForm = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [rowEdits, setRowEdits] = useState({});
  const {
    pendingWithdrawals,
    submitLoading,
  } = useSelector((state) => state.bankOperations);

  useEffect(() => {
    dispatch(fetchPendingWithdrawals());
  }, [dispatch]);

  const updateRowEdit = useCallback((txnId, patch) => {
    setRowEdits((prev) => ({
      ...prev,
      [txnId]: {
        ...prev[txnId],
        ...patch,
      },
    }));
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "Txn ID",
        dataIndex: "txnId",
        key: "txnId",
        width: 140,
      },
      {
        title: "Type",
        dataIndex: "requesterType",
        key: "requesterType",
        width: 100,
        render: (value) => (value === "investor" ? "Investor" : "Business"),
      },
      {
        title: "Organization",
        dataIndex: "orgName",
        key: "orgName",
        render: (value, record) => value || record.orgId || "-",
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 130,
        render: (value) => formatCurrency(value),
      },
      {
        title: "Payout bank",
        key: "bank",
        width: 180,
        render: (_, record) =>
          record.beneficiaryAccountLast4
            ? `${record.beneficiaryBankName || "Bank"} ****${record.beneficiaryAccountLast4}`
            : "-",
      },
      {
        title: "Request date",
        dataIndex: "requestDate",
        key: "requestDate",
        width: 130,
        render: (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "-"),
      },
      {
        title: "UTR / Reference",
        key: "utr",
        width: 200,
        disableDefaultEllipsis: true,
        render: (_, record) => {
          const selected = selectedRowKeys.includes(record.txnId);
          return (
            <Input
              value={rowEdits[record.txnId]?.utr || ""}
              disabled={!selected || submitLoading}
              maxLength={UTR_MAX_LENGTH}
              placeholder="Payout UTR"
              onChange={(e) =>
                updateRowEdit(record.txnId, {
                  utr: (e.target.value || "").replace(/\s/g, ""),
                })
              }
            />
          );
        },
      },
      {
        title: "Txn date",
        key: "transactionDate",
        width: 170,
        disableDefaultEllipsis: true,
        render: (_, record) => {
          const selected = selectedRowKeys.includes(record.txnId);
          return (
            <DatePicker
              value={rowEdits[record.txnId]?.transactionDate || null}
              disabled={!selected || submitLoading}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              disabledDate={(current) => current && current > dayjs().endOf("day")}
              onChange={(value) => updateRowEdit(record.txnId, { transactionDate: value })}
            />
          );
        },
      },
    ],
    [rowEdits, selectedRowKeys, submitLoading, updateRowEdit]
  );

  const onSelectChange = (keys) => {
    setSelectedRowKeys(keys);
  };

  const resetSelection = () => {
    setSelectedRowKeys([]);
    setRowEdits({});
    form.resetFields(["remarks"]);
  };

  const onFinish = async (values) => {
    if (!selectedRowKeys.length) {
      message.warning("Select at least one withdrawal request");
      return;
    }

    const selectedItems = (pendingWithdrawals || []).filter((item) =>
      selectedRowKeys.includes(item.txnId)
    );

    const validationErrors = [];
    const utrs = [];
    const items = selectedItems.map((item) => {
      const edit = rowEdits[item.txnId] || {};
      const utr = (edit.utr || "").trim();
      const utrError = validateUtrValue(utr);
      if (utrError) {
        validationErrors.push(`${item.txnId}: ${utrError}`);
      }
      utrs.push(utr);
      return {
        txnId: item.txnId,
        utr,
        transactionDate: toTransactionDateIso(edit.transactionDate),
        remarks: values.remarks?.trim() || undefined,
        isDuplicate: Boolean(values.isDuplicate),
      };
    });

    if (validationErrors.length) {
      message.error(validationErrors[0], 5);
      return;
    }

    if (new Set(utrs).size !== utrs.length) {
      message.error("Each selected withdrawal must have a unique UTR", 5);
      return;
    }

    const result = await dispatch(
      confirmManualWithdrawalsBulk({
        items,
        status: "SUCCESS",
        isDuplicate: Boolean(values.isDuplicate),
      })
    );

    if (confirmManualWithdrawalsBulk.fulfilled.match(result)) {
      const payload = result.payload || {};
      const successCount = payload.successCount ?? payload.succeeded?.length ?? 0;
      const failed = payload.failed || [];
      const failedCount = payload.failedCount ?? failed.length;
      const asDuplicate = Boolean(values.isDuplicate);

      if (failedCount === 0) {
        message.success(
          asDuplicate
            ? `${successCount} withdrawal payout(s) recorded as duplicate`
            : `${successCount} withdrawal payout(s) recorded successfully`
        );
        resetSelection();
        form.resetFields(["remarks", "isDuplicate"]);
      } else if (successCount === 0) {
        message.error(failed[0]?.error || "Failed to record withdrawal payouts", 5);
      } else {
        message.warning(
          `${successCount} recorded, ${failedCount} failed${failed[0]?.error ? `: ${failed[0].error}` : ""}`,
          6
        );
        setSelectedRowKeys(failed.map((item) => item.txnId));
      }

      dispatch(fetchPendingWithdrawals());
      dispatch(clearBankOpsMessages());
      return;
    }

    if (confirmManualWithdrawalsBulk.rejected.match(result)) {
      message.error(result.payload || "Failed to record withdrawal payouts", 5);
      dispatch(clearBankOpsMessages());
    }
  };

  return (
    <Card title="Withdrawal payout">
      <Spin spinning={submitLoading}>
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ isDuplicate: false }}>
          <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
            <Text type="secondary">
              Select pending withdrawals and enter a UTR for each to record payouts in bulk.
            </Text>
            <Text>
              Selected: <strong>{selectedRowKeys.length}</strong>
            </Text>
          </Flex>

          <CustomTable
            rowKey="txnId"
            columns={columns}
            dataSource={pendingWithdrawals || []}
            loading={false}
            pagination={(pendingWithdrawals || []).length > 10}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys,
              onChange: onSelectChange,
              getCheckboxProps: () => ({ disabled: submitLoading }),
            }}
            locale={{ emptyText: "No pending withdrawal payouts to record" }}
          />

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={16}>
              <CustomInput
                label="Remarks (optional, applied to all selected)"
                name="remarks"
                isTextArea
                rows={2}
                maxLength={REMARKS_MAX_LENGTH}
                showCount
                rules={getRemarksRules()}
              />
            </Col>
            {SHOW_MARK_AS_DUPLICATE && (
              <Col xs={24}>
                <Form.Item name="isDuplicate" valuePropName="checked" style={{ marginBottom: 8 }}>
                  <Checkbox>
                    Mark selected as duplicate (already posted on another path — do not update wallet)
                  </Checkbox>
                </Form.Item>
              </Col>
            )}
          </Row>

          <CustomButton
            type="primary"
            htmlType="submit"
            loading={submitLoading}
            disabled={!selectedRowKeys.length}
          >
            Record selected ({selectedRowKeys.length})
          </CustomButton>
        </Form>
      </Spin>
    </Card>
  );
};

export default WithdrawalPayoutForm;
